import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 here, but surface unexpected network errors.
      toast.error(err?.response?.data?.detail || t("forgot.toast_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mb-3">
          {t("forgot.eyebrow")}
        </p>
        <h1 className="serif text-5xl">{t("forgot.title")}</h1>
        <p className="text-sm text-[#4A5C50] mt-4">{t("forgot.subtitle")}</p>

        {sent ? (
          <div
            className="mt-10 rounded-2xl border border-[#184A30]/20 bg-[#184A30]/5 p-6"
            data-testid="forgot-password-sent-banner"
          >
            <div className="flex items-start gap-3">
              <CheckCircle size={24} weight="duotone" className="text-[#184A30] shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-[#112318]">{t("forgot.sent_title")}</p>
                <p className="mt-1 text-sm text-[#4A5C50]">
                  {t("forgot.sent_message", { email })}
                </p>
                <p className="mt-3 text-[12px] text-[#4A5C50]">
                  {t("forgot.sent_expiry_note")}
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-4">
              <Link to="/login" className="text-[#184A30] link-ul text-sm" data-testid="forgot-back-to-login">
                {t("forgot.back_to_login")}
              </Link>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-[#4A5C50] link-ul text-sm"
                data-testid="forgot-resend"
              >
                {t("forgot.resend_link")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="forgot-password-form">
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
                  {t("forgot.email_label")}
                </Label>
                <div className="relative">
                  <EnvelopeSimple size={18} weight="duotone" className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A5C50] pointer-events-none" />
                  <input
                    className="underline-input mono pl-8"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("forgot.email_placeholder")}
                    required
                    autoFocus
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
                data-testid="forgot-password-submit"
              >
                {loading ? t("forgot.submitting") : t("forgot.submit")}
              </Button>
            </form>
            <p className="text-sm text-[#4A5C50] mt-8">
              {t("forgot.remember_q")}{" "}
              <Link to="/login" className="text-[#184A30] link-ul" data-testid="forgot-link-login">
                {t("forgot.signin_link")}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
